import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { Spinner } from './Spinner.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-md font-semibold ' +
  'transition-colors duration-150 select-none whitespace-nowrap ' +
  'disabled:cursor-not-allowed disabled:opacity-60 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-base';

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-accent-base text-text-inverse hover:bg-accent-hover active:bg-accent-base ' +
    'shadow-[0_1px_0_0_rgba(255,255,255,0.08)_inset]',
  secondary:
    'bg-bg-surface-2 text-text-primary border border-border-strong ' +
    'hover:bg-bg-surface-3 hover:border-border-strong active:bg-bg-surface-2',
  ghost:
    'bg-transparent text-text-primary hover:bg-bg-surface-2 active:bg-bg-surface-3 ' +
    'border border-transparent',
  danger:
    'bg-danger text-text-inverse hover:bg-danger/90 active:bg-danger/80',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm min-w-[44px]',
  md: 'h-11 px-5 text-base min-w-[44px]',
  lg: 'h-12 px-6 text-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? <Spinner size="sm" label="Working" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});
